"use client";

import { useBoundStore } from "@/lib/stores/useBoundStore";
import styled from "styled-components";
import ProfileIcon from "@/assets/icons/Profile.svg";
import LogoutIcon from "@/assets/icons/LogoutIcon.svg";
import OrgProfileIcon from "@/assets/icons/OrgProfileIcon.svg";
import FilterIcon from "@/assets/icons/FilterIcon.svg";
import CloseIcon from "@/assets/icons/CloseIcon.svg";
import { Icon } from "../Icon/Icon";
import theme from "@/themes/velys-theme";
import { useAuthStore } from "@/lib/stores/useAuthStore";
import { useEffect, useRef, useState } from "react";
import DePuySynthesLogo from "@/assets/logos/DePuySynthes.svg";
import DePuySynthesMonogram from "@/assets/logos/DePuySynthesMonogram.svg";
import Image from "next/image";
import { useMedia } from "@/lib/hooks/useMedia";
import { Modal } from "../Modal/Modal";
import { useTranslations } from "next-intl";
import PatientProfileContent from "../Modal/PatientProfileContent";
import { useGetPatientById } from "@/lib/hooks/usePatient";
import { useGetEnrollmentById } from "@/lib/hooks/useEnrollments";
import CaseDataLoader from "../CaseDataLoader/CaseDataLoader";
import { useUserProfile } from "@/lib/hooks/useUser";
import toast from "react-hot-toast";
import { LimitExceededException } from "@aws-sdk/client-cognito-identity-provider";
import config from "@/config";
import { useAuth } from "react-oidc-context";
import OTPVerification from "../Modal/OTPVerification";
import CustomToast from "../CustomToast/CustomToast";
import { useCognitoApi } from "@/lib/hooks/useCognitoApi";
import { LocaleDropdown } from "../Dropdown/LocaleDropdown";
import { SUPPORTED_LOCALES } from "@/lib/constants/locale";

const HeadNav = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  width: 100vw;
  padding: 12.5px 10px;
  padding-right: 25px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;

  .left,
  .right {
    display: flex;
    align-items: center;
    gap: 5px;

    p {
      font-size: 1.15rem;
    }
  }

  .right {
    gap: 20px;
  }

  > img:not([width="15"]) {
    max-height: 45px;
    height: 45px;
    width: auto;
    object-fit: contain;
  }

  @media (max-width: 500px) {
    .right {
      gap: 10px;
    }

    .left {
      gap: 4px;
      min-width: 0;
    }

    .left p {
      font-size: 0.85rem;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9998; /* Make sure this is below nav panel but above everything else */
`;

const MobileNavigationOverlay = styled.div`
  position: fixed;
  top: 0;
  min-width: fit-content;
  width: 40%;
  height: 100%;
  max-height: 100dvh;
  overflow-y: auto;
  background: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  z-index: 9999;

  &.filter {
    right: 0;
    transform: translateX(100%);

    .nav-p {
      font-size: 1.25rem;
      color: ${(props) => props.theme.main};
    }
  }

  &.menu {
    left: 0;
    transform: translateX(-100%);
  }

  &.open {
    transform: translateX(0);
  }

  .items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .opened-chevron {
    transform: rotate(180deg);
  }
`;

const MobileFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  padding: 0 15px 15px 15px;
`;

export default function PatientHeadNav() {
  const tPatientProfile = useTranslations("patientProfile");
  const tComponentText = useTranslations("componentText");

  const user_profile = useAuthStore((state) => state.user_profile);
  const signOutRedirect = useBoundStore((state) => state.signOutRedirect);
  const setUserProfile = useAuthStore((state) => state.setUserProfile);
  const orgProfile = useBoundStore((state) => state.orgProfile);
  const patientEnrollment = useBoundStore((state) => state.patientEnrollment);
  const setPatientEnrollment = useBoundStore(
    (state) => state.setPatientEnrollment
  );
  const userLicenses = useBoundStore((state) => state.userLicenses);
  const setUserLicenses = useBoundStore((state) => state.setUserLicenses);

  const modalType = useBoundStore((state) => state.modalType);
  const isModalOpen = useBoundStore((state) => state.isModalOpen);
  const setModalType = useBoundStore((state) => state.setModalType);
  const setModalOpen = useBoundStore((state) => state.setModalOpen);

  const filterComponent = useBoundStore((state) => state.filterComponent);
  const isOverlay = useBoundStore((state) => state.overlayType);
  const setIsOverlay = useBoundStore((state) => state.setOverlayType);

  // locale dropdown (navigation handled by parent HeadNavigation effect)
  const selectedLocale = useBoundStore((state) => state.selectedLocale);
  const setSelectedLocale = useBoundStore((state) => state.setSelectedLocale);
  const isEditingProfile = useBoundStore((state) => state.isEditingProfile);

  const userId = user_profile?.sub;
  const patientQuery = useGetPatientById(userId);
  const patientQueryEnrollmentId = patientQuery.data?.enrollment_id || "";
  const enrollmentQuery = useGetEnrollmentById(patientQueryEnrollmentId);

  // get user profile
  const { userProfileQuery } = useUserProfile(enrollmentQuery.data?.surgeon_id);

  // fetch userLicenses if we refresh the page and first load
  useEffect(() => {
    if (userProfileQuery.data) {
      setUserLicenses(userProfileQuery.data.licenses || {});
    }
  }, [userProfileQuery.data]);

  const [isVerifyPhone, setIsVerifyPhone] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const cooldownTimer = useRef<NodeJS.Timeout | null>(null);

  const max768w = useMedia("(max-width: 768px)");
  const max500w = useMedia("(max-width: 500px)");
  const max475w = useMedia("(max-width: 475px)");
  const logoSize = max500w ? 28 : 45;

  const { sendOTPCodeMutation } = useCognitoApi();

  useEffect(() => {
    if (patientQuery.data && enrollmentQuery.data) {
      const enrollment = {
        org_id: enrollmentQuery.data.organization_id || "",
        patient: patientQuery.data,
        enrollment: enrollmentQuery.data,
      };

      setPatientEnrollment(enrollment);
    }
  }, [patientQuery.data, enrollmentQuery.data]);

  //get org profile based on enrollment
  useEffect(() => {
    if (patientEnrollment?.org_id && user_profile) {
      //test data
      const updatedProfile = {
        ...user_profile,
        orgid: [patientEnrollment?.org_id],
      };
      setUserProfile(updatedProfile);
    }
  }, [patientEnrollment?.org_id]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    };
  }, []);

  const startResendCooldown = () => {
    setIsResendDisabled(true);

    cooldownTimer.current = setTimeout(() => {
      setIsResendDisabled(false);
    }, 60000); // 60 seconds = 60000 milliseconds
  };

  const handleViewPatientProfile = () => {
    setModalType("view-patient-profile");
    setModalOpen(true);
  };

  const handleClosePatientProfile = () => {
    setModalType("");
    setModalOpen(false);
  };

  const handleVerifyPhoneNumber = () => {
    sendOTPCode("initial");
    setIsVerifyPhone(true);
  };

  const sendOTPCode = (trigger?: string) => {
    sendOTPCodeMutation.mutate(trigger, {
      onSuccess: () => {
        if (trigger === "initial") {
          toast.custom((t) => (
            <CustomToast t={t} message={tComponentText("sendInitialCode")} />
          ));
        } else {
          toast.custom((t) => (
            <CustomToast t={t} message={tComponentText("sendResendCode")} />
          ));
        }
        startResendCooldown();
      },
      onError: (error) => {
        if (error instanceof LimitExceededException) {
          toast.custom((t) => (
            <CustomToast t={t} message={tComponentText("errorLimitExceeded")} />
          ));
        } else {
          toast.custom((t) => (
            <CustomToast
              t={t}
              message={tComponentText("errorVerificationFailed")}
            />
          ));
          setIsResendDisabled(false);
        }
      },
    });
  };

  return !patientEnrollment ? null : (
    <HeadNav>
      <div className="left">
        {patientEnrollment?.enrollment.status ===
        "INACTIVE" ? null : !orgProfile ? (
          <CaseDataLoader percent={null} size={`${logoSize}px`} />
        ) : userLicenses &&
          userLicenses?.["jnj.velys.patientpath.custom"] !== true ? (
          <Image
            src={DePuySynthesLogo.src}
            alt="DePuy Synthes"
            width={297}
            height={45}
            style={{ height: `${logoSize}px` }}
          />
        ) : (
          <>
            {!orgProfile ? (
              <CaseDataLoader percent={null} size="25px" />
            ) : orgProfile?.logos &&
              (orgProfile?.logos["icon"] || orgProfile?.logos["logo"]) ? (
              orgProfile.logos["icon"] ? (
                <>
                  <img
                    style={{ cursor: "pointer" }}
                    src={orgProfile.logos["icon"]}
                    alt="org-logo"
                    width={logoSize}
                    height={logoSize}
                  />
                  <p>{orgProfile?.name}</p>
                </>
              ) : (
                <img
                  style={{
                    cursor: "pointer",
                    maxHeight: `${logoSize}px`,
                    width: "auto",
                  }}
                  src={orgProfile.logos["logo"]}
                  alt="org-logo"
                  width={180}
                  height={300}
                />
              )
            ) : (
              <>
                <img
                  src={DePuySynthesMonogram.src}
                  alt="DePuy Synthes"
                  width={logoSize}
                  height={logoSize}
                  style={{ height: `${logoSize}px`, width: "auto" }}
                />
                <p
                  style={{
                    height: logoSize,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: 0,
                  }}
                >
                  {orgProfile?.name}
                </p>
              </>
            )}
            <Image
              style={{ cursor: "pointer", marginLeft: "5px" }}
              src={OrgProfileIcon.src}
              alt="view-healthcare-team-icon"
              width={15}
              height={15}
              onClick={() => {
                setModalOpen(true);
                setModalType("healthcare-team");
              }}
            />
          </>
        )}
      </div>
      <div className="right">
        {patientEnrollment?.enrollment.status === "INACTIVE" ? null : (
          <>
            {filterComponent && (
              <Icon
                url={FilterIcon.src}
                color={theme.main}
                height={max500w ? "16px" : "20px"}
                width={max500w ? "16px" : "20px"}
                onPress={() => setIsOverlay("filters")}
              />
            )}
            <Icon
              url={ProfileIcon.src}
              color={theme.main}
              height={max500w ? "24px" : "31px"}
              width={max500w ? "24px" : "31px"}
              onPress={handleViewPatientProfile}
            />
          </>
        )}
        <Icon
          url={LogoutIcon.src}
          color={theme.main}
          height={max500w ? "20px" : "25px"}
          width={max500w ? "20px" : "25px"}
          onPress={() => signOutRedirect()}
        />
        {!max475w && (
          <LocaleDropdown
            options={SUPPORTED_LOCALES}
            selectedOption={selectedLocale}
            onSelect={(e) => {
              const value = (e.target as HTMLLIElement).getAttribute(
                "data-value"
              );
              setSelectedLocale(value!);
            }}
            color={theme.main}
            backgroundColor={theme.white}
          />
        )}
      </div>
      {isModalOpen && modalType === "view-patient-profile" && (
        <Modal
          modalWidth="50%"
          modalHeight="95%"
          title={tPatientProfile("title")}
          toggleModal={() => handleClosePatientProfile()}
          content={
            <PatientProfileContent
              handleVerifyPhoneNumber={handleVerifyPhoneNumber}
            />
          }
          headerAction={
            max475w ? (
              <LocaleDropdown
                options={SUPPORTED_LOCALES}
                selectedOption={selectedLocale}
                onSelect={(e) => {
                  const value = (e.target as HTMLLIElement).getAttribute(
                    "data-value"
                  );
                  setSelectedLocale(value!);
                }}
                color={theme.main}
                backgroundColor={theme.white}
                disabled={isEditingProfile}
              />
            ) : undefined
          }
          showtimesIcon
          needFullScreen={max768w}
        />
      )}
      {isOverlay === "filters" && (
        <>
          <MobileOverlay onClick={() => setIsOverlay("")} />
          <MobileNavigationOverlay
            className={`filter ${MobileOverlay !== "" && "open"}`}
          >
            <div
              className="mobile-container"
              style={{
                width: "100%",
                borderBottom: `1px solid ${theme.main}`,
                padding: "15px 10px 10px 10px",
                display: "flex",
                gap: 10,
                alignItems: "center",
              }}
            >
              <Icon
                url={CloseIcon.src}
                color={theme.main}
                height="20px"
                width="20px"
                onPress={() => setIsOverlay("")}
              />
              <p className="nav-p">Filters</p>
            </div>
            <MobileFilters>{filterComponent}</MobileFilters>
          </MobileNavigationOverlay>
        </>
      )}
      {isVerifyPhone && (
        <Modal
          title={
            !max768w
              ? tComponentText("verifyPhoneNumber")
              : tComponentText("verifySmaller")
          }
          content={
            <OTPVerification
              closeModal={() => setIsVerifyPhone(false)}
              sendOTPCode={sendOTPCode}
              isResendDisabled={isResendDisabled}
            />
          }
          toggleModal={() => {}}
        />
      )}
    </HeadNav>
  );
}
